<template>
  <step-template :subStep="subStep">
    <player
      :video="video"
      :startTime="startTime"
      :isMobile="isMobile"
      @onPlay="playerStateChanged"
      @onPause="playerStateChanged"
      @onStateChange="playerStateChanged"
      @onPercentChanged="watchedPercentChanged"
    />
    <v-row v-if="messageAbove" style="width: 100%;">
      <v-col>
        <h5 
          class="text-center text-break mx-8 mt-5 mb-0" 
          v-html="messageAbove"
          >
        </h5>
      </v-col>
    </v-row>
    <v-row v-if="messageBox">
      <v-col>
        <v-sheet
        class="pa-6 mx-8 mt-5 mb-0"
        elevation="3"
        rounded
        :color=messageBoxBgColor
      >
        <h5 
          class="text-center text-break ma-0" 
          v-html="messageBox"
          :style="`color:${messageBoxTxtColor}`"
          >
        </h5>
      </v-sheet>
      </v-col>
    </v-row>
    <v-row v-if="messageBelow" style="width: 100%;">
      <v-col>
        <h5 
          class="text-center text-break mx-8 mt-5 mb-0" 
          v-html="messageBelow"
          >
        </h5>
      </v-col>
    </v-row>
    <v-row v-if="actionButtons" class="mt-5 justify-center">
      <v-col v-if="!isMobile" class="d-flex justify-center">
        <v-btn
          v-for="(button, key) in actionButtons"
          :key="key"
          :class="`${button.class} mr-4`"
          large
          elevation="2"
          rounded
          :color="button.color"
          @click="customButtonClick(button)"
        >
          <span>{{ button.label }}</span>
        </v-btn>
      </v-col>
      <v-btn
        v-else
        v-for="(button, key) in actionButtons"
        :key="key"
        :class="`${button.class} mb-2`"
        large
        elevation="2"
        rounded
        :color="button.color"
        @click="customButtonClick(button)"
      >
        <span>{{ button.label }}</span>
      </v-btn>
    </v-row>
    <v-row v-if="cMessage">
      <v-col>
      <h5 
        class="text-center text-break text-gray-750 font-italic mt-5 mb-0" 
        v-html="cMessage"
        >
      </h5>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <h5 class="my-5 text-center text-break text-gray-750 font-italic" v-if="videoWatched" v-html="cVideoWatchedMessage">
        </h5>
      </v-col>
    </v-row>
    <confirm
      ref="confirmModal"
      :showButton="false"
      :title="modal.title"
      :description="modal.description"
      buttonConfirmLabel="Yes"
      buttonConfirmColor="green"
      buttonCancelLabel="No"
      buttonCancelColor="black"
      :buttonConfirmCallback="
        () => {
          this.modal.onConfirm();
        }
      "
    ></confirm>
  </step-template>
</template>

<script>
/**
 * To use:
 - in the database, go to table substeps
 - find the substep row that you want this component
 - update the row field component='step-video'
 - update the row field component_props as a JSON like: 
        
* SIMPLE VIDEO
{ "video_id": "gqzfc4hfjy" }

OR

* Add Messages Boxed = messageBox/Unboxed = message
{
  "video_id":"value",
  "messageBox": "This message shows inside a box with custom color options.",
  "messageBoxTxtColor": "white",
  "messageBoxBgColor": "green",
  "message": "This message shows outside a box.",
}

OR
 
* VIDEO CHOOSE BY VARIABLE (like variables set in stepButtons.vue)
{
  "video_id": {
    "op": "value",
    "variable": "CUSTOM_VARIABLE_NAME",
    "values": {
      "VIDEO_ID": "CUSTOM_VARIABLE_VALUE1",
      "VIDEO_ID": "CUSTOM_VARIABLE_VALUE2",
      "VIDEO_ID": "CUSTOM_VARIABLE_VALUE3",
    }
  }
}

OR

* VIDEO CHOOSE BY VARIABLE, AND VARIABLE VALUE BETWEEN A RANGE (like variables set in stepSlider.vue)
{
  "video_id": {
    "op": "range",
    "variable": "CUSTOM_VARIABLE_NAME",
    "values": {
      "tmrf458pl1": { "min": 0, "max": 6.5 },
      "hfgqrdahv0": { "min": 6.6, "max": 7.9 },
      "9byj5tf894": { "min": 8, "max": 12 }
    }
  }
}

OR

* VIDEO CHOOSE BY DATASET VARIABLE (supports: "caseData" (default), "avatar", "user")
* Also supports nested properties like "avatar.id" or "user.email"
{
  "video_id": {
    "op": "value",
    "dataset": "avatar",
    "variable": "id",
    "values": {
      "7hvxywjbe8": "1",
      "t3szvnl8mb": "2",
      "jgavgq2auq": "3"
    }
  }
}

ALSO

* to use action buttons (can be combined with the other video modes above):
 {
  "message": "You have completed CASE 2",
  "actionButtons": [
    {
      "label": "Go to library",
      "color": "warning",
      "action": "show-library",
      "class": "caption font-weight-bold"
    },
    {
      "label": "Claim credit",
      "color": "success",
      "action": "show-credit",
      "class": "caption font-weight-bold"
    },
    {
      "label": "Move to substep 20",
      "color": "warning",
      "action": "changeSubStep",
      "actionValue": "20",
      "class": "caption font-weight-bold"
    },
        {
      "label": "Begin next case",
      "color": "warning",
      "action": "changeStep",
      "actionValue": "3",
      "class": "caption font-weight-bold"
    }
  ],
  "video_id": "ww55n6mfyg"
}
 */

import { mapState, mapActions, mapMutations } from "vuex";
import axios from "axios";
import Confirm from "/src/components/modals/Confirm.vue";
import Player from "/src/components/player/Player.vue";
import StepTemplate from "/src/components/steps/StepTemplate.vue";

const VIDEO_PERCENT_AS_WATCHED = 95;
const VIDEO_SECONDS_REMAINING_AS_WATCHED = 3;

export default {
  name: "StepVideo",
  components: {
    Confirm,
    Player,
    StepTemplate,
  },
  props: {
    steps: {
      type: Array,
      required: true,
    },
    step: {
      type: Object,
      required: true,
      default: () => {
        return {};
      },
    },
    subStep: {
      type: Object,
      required: true,
      default: () => {
        return {};
      },
    },
    video_id: {
      type: [String, Object],
      required: true,
      default: "",
    },
    messageAbove: {
      type: String,
      required: false,
      default: "",
    },
    messageBox: {
      type: String,
      required: false,
      default: "",
    },
    messageBoxBgColor: {
      type: String,
      required: false,
      default: "#f0faff",
    },
    messageBoxTxtColor: {
      type: String,
      required: false,
      default: "#304156",
    },
    messageBelow: {
      type: String,
      required: false,
      default: "",
    },
    message: {
      type: String,
      required: false,
      default: "",
    },
    actionButtons: {
      type: Array,
      required: false,
      default: null,
    },
    isMobile: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      video: {
        id: "",
      },
      previouslyTag: false,
      startTime: 0,
      videoWatched: false,
      videoIsOptional: false,
      modal: {
        title: "",
        description: "",
        onConfirm: () => {
          console.log("not set");
        },
      },
    };
  },
  computed: {
    ...mapState({
      caseData: (state) => state.caseData,
      avatar: (state) => state.avatar,
      user: (state) => state.user,
      continueButtonEnabled: (state) => state.continueButtonEnabled,
      isLastSubStepFromLastStep: (state) => state.isLastSubStepFromLastStep,
    }),
    cVideoWatchedMessage() {
      // Required Video
      var tag = "";
      if (this.videoWatched && this.previouslyTag) {
        tag = "You previously completed this video.";
      } else if (this.videoWatched && !this.previouslyTag) {
        tag = "You completed this video.";
      }

      if (!this.isLastSubStepFromLastStep) {
        tag += "<br/>Click <span style='color:#ff9800;'><b>CONTINUE</b></span> or play again to review.";
      }
      return tag;
    },
    cMessage() {
      // Message shows at start
      if (this.message) {
        return this.message;
      }
      // Pre-Watched Message
      // Not Optional
      if (!this.videoIsOptional && !this.videoWatched) {
        return "Please finish the video before continuing.";
      // Optional
      } else if (this.videoIsOptional && !this.videoWatched) {
        return "Feel free to watch the video above or click <span style='color:#ff9800;'><b>CONTINUE</b></span>.";
      }
      if (this.cVideoWatchedMessage === "") {
        return "When you complete the video, click <span style='color:#ff9800;'><b>CONTINUE</b></span>.";
      }
      return "";
    },
  },
  watch: {
    videoWatched(val) {
      this.toggleContinueButton(val);
    },
    videoIsOptional() {
      this.whenVideoIsOptional();
    },
  },
  created() {
    this.setVideoIdFromProp();
    this.whenVideoIsOptional();
    this.checkWatchedProgress();
  },
  methods: {
    ...mapMutations([
      "setCurrentStep",
      "setCurrentSubStep",
      "setShowCredit",
      "setShowLibrary",
      "setShowCaptions",
      "setContinueButtonEnabled",
    ]),
    ...mapActions(["setCaseData"]),
    whenVideoIsOptional() {
      this.toggleContinueButton(this.videoIsOptional);
    },
    toggleContinueButton(enable) {
      if (this.isLastSubStepFromLastStep) {
        this.setContinueButtonEnabled(false);
      } else if (this.continueButtonEnabled !== enable) {
        this.setContinueButtonEnabled(enable);
      }
    },
    setVideoIdFromProp() {
      if (typeof this.video_id === "string") {
        this.video.id = this.video_id;
      } else {
        const videoOptions = this.video_id;
        if (!videoOptions.op) {
          this.video.id = this.video_id.value;
          this.setVideoCustomOptions(this.video_id);
        }
        if (videoOptions.op === "range") {
          this.video.id = this.getVideoIdByRange(videoOptions);
        } else if (videoOptions.op === "value") {
          this.video.id = this.getVideoIdByValue(videoOptions);
        }
      }
    },
    getDataSet(config) {
      if (config.dataset === undefined || config.dataset === null || config.dataset === "caseData") {
        return this.caseData;
      } else if (config.dataset === "user") {
        return this.user;
      } else if (config.dataset === "avatar") {
        return this.avatar;
      }
      return this.caseData;
    },
    getVariableValue(dataSet, variableName) {
      if (!dataSet || !variableName) return undefined;
      
      // Support nested properties like "avatar.id" or "user.email"
      if (variableName.includes('.')) {
        const parts = variableName.split('.');
        let value = dataSet;
        for (const part of parts) {
          if (value === undefined || value === null) return undefined;
          value = value[part];
        }
        return value;
      }
      
      return dataSet[variableName];
    },
    getVideoIdByRange(videoOptions) {
      const dataSet = this.getDataSet(videoOptions);
      const valueToCompare = this.getVariableValue(dataSet, videoOptions.variable);
      for (const videoHash in videoOptions.values) {
        const values = videoOptions.values[videoHash];
        if (values.min <= valueToCompare && valueToCompare <= values.max) {
          if (values) this.setVideoCustomOptions(values);
          return videoHash;
        }
      }
      return null;
    },
    getVideoIdByValue(videoOptions) {
      const dataSet = this.getDataSet(videoOptions);
      const valueToCompare = this.getVariableValue(dataSet, videoOptions.variable);
      for (const videoHash in videoOptions.values) {
        let value = videoOptions.values[videoHash];
        let options;
        if (typeof value !== "string") {
          options = value;
          value = value.value;
        }
        if (value == valueToCompare) {
          if (options) this.setVideoCustomOptions(options);
          return videoHash;
        }
      }
      return null;
    },
    setVideoCustomOptions(options) {
      if (options.isOptional) {
        this.videoIsOptional = true;
      }
    },
    customButtonClick(button) {
      if (!button.action) {
        return;
      }
      if (button.action === "changeSubStep") {
        this.setCurrentSubStep(parseInt(button.actionValue));
      } else if (button.action === "changeStep") {
        this.clearCurrentCaseData();
        this.setCurrentStep(parseInt(button.actionValue));
        const firstSubStepId = this.step.sub_steps[0].id;
        this.setCurrentSubStep(firstSubStepId);
      } else if (button.action === "show-credit") {
        this.modalShowCredits();
      } else if (button.action === "show-library") {
        this.modalShowLibrary();
      }
    },
    clearCurrentCaseData() {
      this.setCaseData({});
    },
    modalShowCredits() {
      this.setShowCredit(true);
    },
    modalShowLibrary() {
      this.setShowLibrary(true);
    },
    playerStateChanged(playerData) {
      if (!playerData || !playerData.event) return;
      if (["pause", "beforeDestroy"].indexOf(playerData.event) > -1) {
        this.saveWatchedProgress(playerData);
      }
    },
    watchedSecondsChanged(videoOptions) {
      if (
        !this.videoWatched &&
        videoOptions.secondsWatched >=
          videoOptions.duration - VIDEO_SECONDS_REMAINING_AS_WATCHED
      ) {
        this.videoWatched = true;
      }
    },
    watchedPercentChanged(videoOptions) {
      // Rule of set video as watched when above VIDEO_PERCENT_AS_WATCHED replaced to VIDEO_SECONDS_REMAINING_AS_WATCHED
      // if (videoOptions.percentWatched >= VIDEO_PERCENT_AS_WATCHED) {
      //   this.videoWatched = true;
      // }
      this.watchedSecondsChanged(videoOptions);
    },
    async saveWatchedProgress(playerData) {
      const payload = {
        seconds_watched: playerData.secondsWatched,
        watched: this.videoWatched,
      };
      try {
        await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/videos-users/${playerData.videoId}/${this.user.uuid}`,
          payload
        );
      } catch (err) {
        console.log(err);
      }
    },
    async checkWatchedProgress() {
      let videoUserData;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/videos-users/${this.video.id}/${this.user.uuid}`
        );
        videoUserData = response.data;
      } catch (err) {
        console.log(err);
      }

      if (!videoUserData) {
        return;
      }

      if (videoUserData.watched) {
        this.videoWatched = true;
        this.previouslyTag = true;
      }

      if (videoUserData.seconds_watched) {
        this.startTime = videoUserData.seconds_watched;
      }
    },
  },
};
</script>
